<template>
  <div
    class="details-container"
    @click.stop=""
  >
    <Flicking
      v-if="photos.length > 0"
      ref="flicking"
      :options="options"
      :plugins="plugins"
      style="height: 90%"
    >
      <div
        v-for="(image, index) in photos"
        :key="index"
      >
        <div
          :id="`large-image-${index}`"
          ref="image"
          :style="{ backgroundImage: 'url(' + image + ')' }"
          class="large-image"
        />
        <rotate-image-buttons
          v-if="controls"
          :ref="`rotateControls${index}`"
          :image-container-id="`large-image-${index}`"
          style="z-index: 10;"
          :save-disabled="saveDisabled"
          :show-download-button="true"
          :photo-url="getPhotoUrl(index)"
          :photo-id="photoIds[index]"
          @save-clicked="saveRotation"
          @download-image="handleDownload"
        />
      </div>
        <template #viewport>
            <span class="flicking-arrow-prev"></span>
            <span class="flicking-arrow-next"></span>
        </template>
    </Flicking>
    <font-awesome-icon
      icon="times-circle"
      class="close-dialog-icon colored-border"
      @click.stop="$emit('close')"
    />
    <Transition name="fade">
      <div
        v-if="photos.length === 0"
        class="fullscreen-image-loading"
      >
        {{ $t('common.loading') }}
      </div>
    </Transition>
  </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {Arrow} from "@egjs/flicking-plugins";
import Flicking from "@egjs/vue3-flicking";
import RotateImageButtons from '@/components/view/RotateImageButtons'
import { downloadHelper } from '../mixins/DownloadMixin'

export default {
    name: 'FullscreenImage',
    components: {Flicking, RotateImageButtons},
    mixins: [restApi, downloadHelper],
    props: {
        url: null,
        largeImages: {
            type: Array,
            default() {
                return []
            }
        },
        panelIndex: {
            type: Number,
            default() {
                return 0
            }
        },
        controls: {
            type: Boolean,
            default() {
                return true
            }
        },
        saveDisabled: {
            type: Boolean,
            default: true,
        },
        photoIds: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            plugins: [new Arrow()],
            options: {
                preventDefaultOnDrag: true,
                circular: true,
                panelsPerView: 1,
            },
            photos: [],
            imageRefs: [],
            loading: false,
        }
    },
    computed: {
        currentIndex() {
            return this.$refs.flicking?.index ?? 0
        },
        photoId() {
            return this.photoIds?.[this.currentIndex] ?? null
        }
    },
    watch: {
        photos: {
            handler() {
                this.$nextTick(() => {
                    this.imageRefs = this.$refs.image;
                });
            },
            deep: true
        }
    },

    mounted() {
        this.photos = []
        if (this.largeImages.length > 0) {
            this.largeImages.forEach(image => {
                this.photos.push(image.src)
            })
            let index = this.panelIndex === undefined ? 0 : this.panelIndex
            if (index > 0) {
                this.$nextTick(() => {
                    //refs requires that element is rendered
                    this.$refs.flicking.moveTo(index)
                })
            }

        } else {
            this.fetchPhoto()
        }

    },
    methods: {
        getCurrentRotateControl() {
            const controls = this.$refs[`rotateControls${this.currentIndex}`]
            return Array.isArray(controls) ? controls[0] : controls
        },
        resetCurrentRotation() {
            this.getCurrentRotateControl()?.resetRotation()
        },
        getPhotoUrl(index = this.currentIndex) {
            return this.largeImages[index]?.url || 
                   this.largeImages[index]?.src || 
                   this.photos[index] || null
        },
        downloadBlob(blob, filename) {
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = filename
            document.body.appendChild(link)
            link.click()
            setTimeout(() => {
                document.body.removeChild(link)
                URL.revokeObjectURL(link.href)
            }, 0)
        },
        saveRotation(rotation) {
            this.resetCurrentRotation()
            this.loading = true
            this.restUpdate(`${this.photoRotationUrl}/${this.photoId}/${rotation}`, {},
                this.rotationSaveResponse, this.rotationSaveResponse)
        },
        rotationSaveResponse() {
            this.loading = false
            this.refreshCurrentPhoto(this.currentIndex)
        },
        refreshCurrentPhoto(index) {
            const currentImageView = this.largeImages[index]
            if (currentImageView && currentImageView.onFileChanged) {
                currentImageView.onFileChanged(currentImageView.file)

                this.$nextTick(() => {
                    this.photos[index] = currentImageView.src
                    this.$emit('close')
                })
            }
        },
        fetchPhoto() {
            this.loading = true
            this.restFetch(this.url, this.handleResponse)
        },
        handleResponse(response) {
            if (response && response.status === 200) {
                this.photos.push(response.data)
            }
        },
        getImageReference: function (index) {
            return this.imageRefs[index]
        },
        handleDownload({ id }) {
            const src = this.photos[this.currentIndex]
            if (!src) return
            this.downloadBase64Image(src, `photo_${id || 'image'}.jpg`)
        }
    }
}
</script>

<style>

.large-image {
    overflow: hidden;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    Transition: transform 0.3s ease;
}

.colored-border {
    border: 0.07em solid black;
    background-color: black;
    border-radius: 50%;
}

</style>
